import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { isImageFileUrl, isPdfFileUrl, renderPdfThumbnailDataUrl } from '../../utils/documentImport';

function fileExtensionLabel(name = '', url = '') {
  const fromName = String(name || '').match(/\.([a-z0-9]{2,5})$/i)?.[1]?.toUpperCase();
  if (fromName) return fromName;
  if (isPdfFileUrl(url, name)) return 'PDF';
  return 'FILE';
}

export function DayFileThumbnail({
  url,
  name,
  onPress,
  theme,
  variant = 'card',
  accessibilityLabel,
}) {
  const fileUrl = String(url || '').trim();
  const [thumbUri, setThumbUri] = useState('');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const isImage = isImageFileUrl(fileUrl);
  const isPdf = isPdfFileUrl(fileUrl, name);
  const styles = useMemo(() => createStyles(theme, variant), [theme, variant]);
  const extLabel = fileExtensionLabel(name, fileUrl);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);

    if (!fileUrl) {
      setThumbUri('');
      setLoading(false);
      return undefined;
    }

    if (isImage) {
      setThumbUri(fileUrl);
      setLoading(false);
      return undefined;
    }

    if (!isPdf || Platform.OS !== 'web') {
      setThumbUri('');
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setThumbUri('');
    renderPdfThumbnailDataUrl(fileUrl, variant === 'detail' ? 640 : 280)
      .then((dataUrl) => {
        if (!cancelled) {
          setThumbUri(dataUrl);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fileUrl, isImage, isPdf, name, variant]);

  const preview = (() => {
    if (loading) {
      return (
        <View style={styles.previewFrame}>
          <ActivityIndicator size="small" color={theme.brand.accent} />
        </View>
      );
    }
    if (thumbUri) {
      return (
        <Image source={{ uri: thumbUri }} style={styles.previewImage} resizeMode="cover" />
      );
    }
    return (
      <View style={styles.previewFrame}>
        <Ionicons name={isPdf ? 'document-text-outline' : 'document-outline'} size={22} color={theme.brand.accent} />
        <Text style={styles.extLabel}>{extLabel}</Text>
        {failed && isPdf ? <Text style={styles.failHint}>Preview unavailable</Text> : null}
      </View>
    );
  })();

  const wrap = onPress ? TouchableOpacity : View;
  const Wrap = wrap;

  return (
    <Wrap
      style={styles.wrap}
      onPress={onPress}
      activeOpacity={onPress ? 0.88 : 1}
      accessibilityLabel={accessibilityLabel || `Preview ${name || 'file'}`}
    >
      {preview}
    </Wrap>
  );
}

function createStyles(theme, variant) {
  const isDetail = variant === 'detail';
  return StyleSheet.create({
    wrap: {
      width: '100%',
      borderRadius: isDetail ? 8 : 6,
      overflow: 'hidden',
      backgroundColor: theme.background.card,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    previewFrame: {
      width: '100%',
      height: isDetail ? 180 : 70,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      backgroundColor: theme.background.secondary,
    },
    previewImage: {
      width: '100%',
      height: isDetail ? 180 : 70,
      backgroundColor: theme.background.secondary,
    },
    extLabel: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.text.secondary,
      letterSpacing: 0.4,
    },
    failHint: {
      fontSize: 9,
      color: theme.text.tertiary,
      textAlign: 'center',
    },
  });
}

export default DayFileThumbnail;
